/**
 * 统一数据表格
 *
 * 财务用户的刚需，这里一次性做掉：
 *   1. 列宽可拖拽调整（核对长字段时必备）
 *   2. 列显示自定义，并按 storageKey 记住偏好（localStorage）
 *   3. 加载 / 失败 / 空 / 演示数据 四态统一，不出现白屏或无限转圈
 *   4. 金额列右对齐等宽由各页面自行在列定义里使用 <Money />
 *   5. 支持导出当前页数据为 CSV（二次核对）
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Checkbox, Dropdown, Space, Table, Tooltip, Typography } from 'antd';
import type { TableProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SettingOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ApiError } from '@/api/client';
import { EmptyState, ErrorState, FallbackNotice } from './PageState';
import { exportCsv, type ExportColumn } from '@/utils/export';

/* -------------------------------------------------------------------------
 * 可拖拽列宽的表头单元格
 * ---------------------------------------------------------------------- */

type ResizableTitleProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  /** 当前列宽，由 onHeaderCell 注入 */
  width?: number;
  onColumnResize?: (width: number) => void;
};

function ResizableTitle({ onColumnResize, width, children, ...rest }: ResizableTitleProps): React.ReactElement {
  const startRef = useRef<{ x: number; width: number } | null>(null);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      if (!onColumnResize || typeof width !== 'number') return;
      e.preventDefault();
      e.stopPropagation();
      startRef.current = { x: e.clientX, width };

      const onMove = (ev: MouseEvent): void => {
        const start = startRef.current;
        if (!start) return;
        onColumnResize(Math.max(60, start.width + (ev.clientX - start.x)));
      };
      const onUp = (): void => {
        startRef.current = null;
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [onColumnResize, width],
  );

  return (
    <th {...rest} style={{ ...rest.style, position: 'relative' }}>
      {children}
      {onColumnResize ? (
        <span
          role="separator"
          aria-label="调整列宽"
          onMouseDown={handleMouseDown}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'col-resize',
            userSelect: 'none',
            touchAction: 'none',
            zIndex: 2,
          }}
        />
      ) : null}
    </th>
  );
}

/* -------------------------------------------------------------------------
 * 列显隐偏好
 * ---------------------------------------------------------------------- */

function loadHidden(storageKey: string | undefined): string[] {
  if (!storageKey) return [];
  try {
    const raw = window.localStorage.getItem(`ilc.columns.${storageKey}`);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
}

function saveHidden(storageKey: string | undefined, hidden: string[]): void {
  if (!storageKey) return;
  try {
    window.localStorage.setItem(`ilc.columns.${storageKey}`, JSON.stringify(hidden));
  } catch {
    /* ignore */
  }
}

/* -------------------------------------------------------------------------
 * DataTable
 * ---------------------------------------------------------------------- */

export interface DataTableProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  rowKey: TableProps<T>['rowKey'];
  loading?: boolean;
  error?: ApiError | null;
  isFallback?: boolean;
  fallbackReason?: string | null;
  onRetry?: () => void;
  /** 表格上方左侧：筛选条件 */
  toolbar?: React.ReactNode;
  /** 表格上方右侧：操作按钮 */
  extra?: React.ReactNode;
  title?: React.ReactNode;
  emptyText?: React.ReactNode;
  emptyAction?: React.ReactNode;
  /** 顶部提示条（如警示文案） */
  notice?: React.ReactNode;
  /** 导出文件名（不含扩展名）；不传则不显示导出按钮 */
  exportFileName?: string;
  exportColumns?: ExportColumn<T>[];
  /**
   * 紧挨在「导出本页」左边渲染的额外导出入口（如「导出专用报表」）。
   *
   * 【为什么不复用 extra】`extra` 在工具条最右一组的最前面，那里放的是
   * 「上传发票」这类**页头动作**；导出类按钮应当与「导出本页」挨着，
   * 否则同一个功能域的动作被上传按钮隔开，找起来费劲。
   */
  exportExtra?: React.ReactNode;
  /** 关闭列宽拖拽 */
  resizable?: boolean;
  /** 列显隐偏好存储键 */
  storageKey?: string;
  rowSelection?: TableProps<T>['rowSelection'];
  expandable?: TableProps<T>['expandable'];
  onRow?: TableProps<T>['onRow'];
  pagination?: TableProps<T>['pagination'];
  onChange?: TableProps<T>['onChange'];
  scrollX?: number | 'max-content';
  size?: 'small' | 'middle' | 'large';
  /** 行内提示（如批量操作栏） */
  footer?: TableProps<T>['footer'];
  summary?: TableProps<T>['summary'];
}

export function DataTable<T extends object>({
  columns,
  dataSource,
  rowKey,
  loading = false,
  error = null,
  isFallback = false,
  fallbackReason = null,
  onRetry,
  toolbar,
  extra,
  title,
  emptyText = '暂无数据',
  emptyAction,
  notice,
  exportFileName,
  exportColumns,
  exportExtra,
  resizable = true,
  storageKey,
  rowSelection,
  expandable,
  onRow,
  pagination,
  onChange,
  scrollX = 'max-content',
  size = 'small',
  footer,
  summary,
}: DataTableProps<T>): React.ReactElement {
  const [widths, setWidths] = useState<Record<string, number>>({});
  const [hiddenKeys, setHiddenKeys] = useState<string[]>(() => loadHidden(storageKey));

  useEffect(() => {
    setHiddenKeys(loadHidden(storageKey));
  }, [storageKey]);

  const columnKey = useCallback((col: ColumnsType<T>[number], index: number): string => {
    const key = (col as { key?: React.Key }).key;
    if (key !== undefined && key !== null) return String(key);
    const dataIndex = (col as { dataIndex?: unknown }).dataIndex;
    if (typeof dataIndex === 'string') return dataIndex;
    if (Array.isArray(dataIndex)) return dataIndex.join('.');
    return `col-${index}`;
  }, []);

  /** 列显隐 + 列宽合并后的列定义 */
  const mergedColumns = useMemo<ColumnsType<T>>(() => {
    return columns
      .map((col, index) => ({ col, key: columnKey(col, index) }))
      .filter(({ key }) => !hiddenKeys.includes(key))
      .map(({ col, key }) => {
        const width = widths[key] ?? (col.width as number | undefined);
        const base = { ...col, width } as ColumnsType<T>[number] & { width?: number };

        if (!resizable) return base;

        return {
          ...base,
          // 通过 onHeaderCell 把宽度与拖拽回调传给自定义表头单元格
          onHeaderCell: () =>
            ({
              width,
              onColumnResize: (next: number) => setWidths((prev) => ({ ...prev, [key]: next })),
            }) as unknown as React.ThHTMLAttributes<HTMLTableCellElement>,
        };
      });
  }, [columns, hiddenKeys, widths, resizable, columnKey]);

  const columnOptions = useMemo(
    () =>
      columns.map((col, index) => ({
        label: typeof col.title === 'string' ? col.title : columnKey(col, index),
        value: columnKey(col, index),
      })),
    [columns, columnKey],
  );

  const settingsMenu = {
    items: [
      {
        key: 'columns',
        label: (
          <div style={{ minWidth: 180 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ marginBottom: 6 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                列显示（拖拽表头分隔线可调整列宽）
              </Typography.Text>
            </div>
            <Checkbox.Group
              value={columnOptions.map((c) => c.value).filter((v) => !hiddenKeys.includes(v))}
              options={columnOptions}
              onChange={(checked) => {
                const next = columnOptions
                  .map((c) => c.value)
                  .filter((v) => !(checked as string[]).includes(v));
                setHiddenKeys(next);
                saveHidden(storageKey, next);
              }}
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
            />
          </div>
        ),
      },
    ],
  };

  const handleExport = useCallback(() => {
    if (!exportFileName) return;
    const cols: ExportColumn<T>[] =
      exportColumns ??
      (mergedColumns.map((col, index) => ({
        title: typeof col.title === 'string' ? col.title : columnKey(col, index),
        dataIndex: (col as { dataIndex?: string | string[] }).dataIndex,
      })) as ExportColumn<T>[]);
    exportCsv(exportFileName, cols, dataSource);
  }, [exportFileName, exportColumns, mergedColumns, dataSource, columnKey]);

  return (
    <div>
      {notice}
      {isFallback ? <FallbackNotice reason={fallbackReason} onRetry={onRetry} /> : null}

      {toolbar || extra || exportFileName || storageKey ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 12,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 auto', minWidth: 240 }}>{toolbar}</div>
          <Space wrap>
            {extra}
            {exportExtra}
            {exportFileName ? (
              <Tooltip title="导出当前页数据为 CSV（审计导出请用档案库的导出功能）">
                <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={dataSource.length === 0}>
                  导出本页
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip title="刷新">
              <Button icon={<ReloadOutlined />} onClick={onRetry} disabled={!onRetry} />
            </Tooltip>
            <Dropdown menu={settingsMenu} trigger={['click']} placement="bottomRight">
              <Tooltip title="列设置">
                <Button icon={<SettingOutlined />} />
              </Tooltip>
            </Dropdown>
          </Space>
        </div>
      ) : null}

      {title ? <div style={{ marginBottom: 8, fontWeight: 600 }}>{title}</div> : null}

      {error ? (
        <ErrorState error={error} onRetry={onRetry} />
      ) : (
        <Table<T>
          size={size}
          bordered
          rowKey={rowKey}
          columns={mergedColumns}
          dataSource={dataSource}
          loading={{ spinning: loading, tip: '加载中…' }}
          pagination={pagination ?? false}
          onChange={onChange}
          rowSelection={rowSelection}
          expandable={expandable}
          onRow={onRow}
          footer={footer}
          summary={summary}
          scroll={{ x: scrollX }}
          components={{ header: { cell: ResizableTitle } }}
          locale={{
            emptyText: loading ? ' ' : <EmptyState text={emptyText} action={emptyAction} compact />,
          }}
        />
      )}

      {!loading && !error && dataSource.length > 0 ? (
        <div style={{ marginTop: 8, textAlign: 'right' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            共 {dataSource.length} 条 · 表头可拖拽调整列宽，右上角可自定义列显示
          </Typography.Text>
        </div>
      ) : null}
    </div>
  );
}

/** 批量操作提示条（选中若干行后出现） */
export function BatchBar({
  count,
  children,
  onClear,
}: {
  count: number;
  children?: React.ReactNode;
  onClear?: () => void;
}): React.ReactElement | null {
  if (count === 0) return null;
  return (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={
        <Space wrap>
          <span>已选择 {count} 项</span>
          {children}
          {onClear ? (
            <Button type="link" size="small" onClick={onClear}>
              取消选择
            </Button>
          ) : null}
        </Space>
      }
    />
  );
}

export default DataTable;
